/**
 * Avatar — LA tête de quelqu'un ou de quelque chose (lot 5 du catalogue, 01-11-2026) : un fan
 * dans un fil, une créatrice dans un classement, un compte X, une app. Une seule pastille, une
 * seule règle de repli : la photo ; sinon un nœud (un logo) ; sinon, avec une `graine` stable
 * (l'id du fan), un des douze avatars par défaut — toujours le même pour la même graine ; sinon
 * l'initiale du nom en Almarena. Une photo qui ne charge pas retombe sur la suite (une URL
 * Discord périme), et part sans Referer (les avatars Google répondent 403 quand il y en a un).
 *
 * `rond` (défaut : les fils, les listes — Kayla, 11-09-2026, un CRM Telegram à l'appui : « c'est
 * plus stylé comme ça ») ou `arrondi` (un carré très arrondi : les tuiles du classement, les
 * logos). `point` : la présence, en bas à droite. `children` : ce qui se pose par-dessus (la
 * pilule « Toi »). `ChatAvatar` et `Tuile` se dessinent avec.
 *
 *   <Avatar src={fan.photo} graine={fan.id} taille={28} />
 *   <Avatar nom="Trista" taille={44} forme="arrondi" />
 *   <Avatar src={membre.photo} nom={membre.nom} alt={membre.nom} point="vert" />
 */
import { useState, type CSSProperties, type ReactNode } from "react";

import { defaultAvatarFor } from "./defaultAvatar";
import { cn } from "./utils";
import "./surfaces.css";

export interface AvatarProps {
  /** La photo. */
  src?: string | null;
  /** Le nom : son initiale quand il n'y a ni photo ni graine, et le texte alternatif si `alt` manque. */
  nom?: string;
  /** Une graine stable (l'id du fan) : sans photo, un des douze avatars par défaut, toujours le même. */
  graine?: string;
  /** Un nœud à la place d'une photo (un logo, une icône). */
  icone?: ReactNode;
  /** Le côté, en px ou en CSS (`clamp(...)`). Défaut 32. */
  taille?: number | string;
  /** `rond` (défaut) ou `arrondi` (un carré très arrondi). */
  forme?: "rond" | "arrondi";
  /** Un point de présence, en bas à droite : `vert` en ligne, `ambre` absent, `gris` hors ligne. */
  point?: "vert" | "ambre" | "gris";
  /** Pour les lecteurs d'écran. Sans `alt` ni `nom` : décoratif. */
  alt?: string;
  className?: string;
  style?: CSSProperties;
  /** Ce qui se pose par-dessus (une pilule, un badge). */
  children?: ReactNode;
}

export function initiale(nom: string): string {
  const premier = nom.trim().charAt(0);
  return premier ? premier.toLocaleUpperCase("fr-FR") : "?";
}

export function Avatar({ src, nom, graine, icone, taille = 32, forme = "rond", point, alt, className, style, children }: AvatarProps) {
  const cote = typeof taille === "number" ? `${taille}px` : taille;
  const arrondi = forme === "rond" ? "av-rond" : "av-arrondi";
  // L'URL qui a cassé (pas un booléen) : une autre photo a droit à son essai.
  const [cassee, setCassee] = useState<string | null>(null);
  const etiquette = alt ?? nom;

  let contenu: ReactNode;
  if (src && src !== cassee) {
    contenu = <img className={cn("av-img", arrondi)} src={src} alt="" draggable={false} referrerPolicy="no-referrer" loading="lazy" onError={() => setCassee(src)} />;
  } else if (icone !== undefined && icone !== null) {
    contenu = icone;
  } else if (graine) {
    contenu = <img className={cn("av-img", arrondi)} src={defaultAvatarFor(graine)} alt="" draggable={false} loading="lazy" />;
  } else {
    contenu = <span className="av-initiale">{initiale(nom ?? "")}</span>;
  }

  return (
    <span
      role={etiquette ? "img" : undefined}
      aria-label={etiquette}
      aria-hidden={etiquette ? undefined : true}
      className={cn("av", arrondi, className)}
      style={{ width: cote, height: cote, "--av-l": cote, ...style } as CSSProperties}
    >
      {contenu}
      {point && <span className="av-point av-rond" data-ton={point} />}
      {children}
    </span>
  );
}
